import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, tap, catchError, switchMap, filter, take, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { StudentService } from './student.service';
import { VolunteerService } from './volunteer.service';
import { TokenService } from './token.service';
import { BackendStudent } from '../mappers/student.mapper';

export interface AuthUser {
  id: string;
  email: string;
  role: 'student' | 'admin' | 'volunteer';
  accountStatus?: 'pending' | 'active' | 'rejected' | 'inactive';
  comments?: string;
  lastStatusChange?: any;
  primerIngresoRealizado?: boolean;
  folio?: string;
  fullName?: string;
  cartaAceptacion?: string;
  studentId?: string;
  volunteerId?: string;
  fechaInicioFormularioExpediente?: string;
  fechaFinFormularioExpediente?: string;
  fechaEmisionExpediente?: string;
  fechaAceptacionExpediente?: string;
  fechaExpedicionExpediente?: string;
  tiempoLlenadoMinutosExpediente?: number;
}

interface AuthResponse {
  status: string;
  token: string;
  user: AuthUser;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private studentService = inject(StudentService);
  private volunteerService = inject(VolunteerService);
  private tokenService = inject(TokenService);
  private readonly baseUrl = `${environment.apiUrl}/auth`;

  private userSubject = new BehaviorSubject<AuthUser | null>(null);
  private authReadySubject = new BehaviorSubject<boolean>(false);
  user$ = this.userSubject.asObservable();

  constructor() {
    if (this.tokenService.isLoggedIn()) {
      this.loadMe().subscribe({
        next: () => this.authReadySubject.next(true),
        error: () => this.authReadySubject.next(true),
        complete: () => this.authReadySubject.next(true),
      });
    } else {
      this.authReadySubject.next(true);
    }
  }

  hasToken(): boolean {
    return this.tokenService.isLoggedIn();
  }

  isLoggedIn(): Observable<boolean> {
    return this.authReadySubject.pipe(
      filter((ready) => ready),
      switchMap(() => this.user$.pipe(
        map((user) => user !== null)
      )),
      take(1)
    );
  }

  getAuthenticatedUser(): Observable<AuthUser | null> {
    return this.authReadySubject.pipe(
      filter((ready) => ready),
      switchMap(() => this.user$),
      take(1)
    );
  }

  signIn(email: string, password: string): Promise<void> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/login`, { email, password })
      .pipe(
        tap((res) => {
          console.log('LOGIN RESPONSE:', res);
          console.log('USER ROLE:', res.user.role);
        
          this.tokenService.setToken(res.token);
          this.userSubject.next(res.user);
        }),
        map(() => undefined),
      )
      .toPromise()
      .then(() => undefined);
  }

  signUpWithStudent(
    email: string,
    password: string,
    studentData: Partial<BackendStudent>,
  ): Promise<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/signup-student`, {
        email,
        password,
        ...studentData,
      })
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            // Fallback transparente si el proceso backend aún no recarga /signup-student
            return this.http.post<AuthResponse>(`${this.baseUrl}/signup`, {
              email,
              password,
              ...studentData,
            });
          }
          return throwError(() => err);
        }),
        tap((res) => {
          this.tokenService.setToken(res.token);
          this.userSubject.next(res.user);
        }),
      )
      .toPromise() as Promise<AuthResponse>;
  }

  signUpWithVolunteer(
    email: string,
    password: string,
    volunteerData: Record<string, any>,
  ): Promise<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        `${this.baseUrl}/signup-volunteer`,
        {
          email,
          password,
          ...volunteerData,
        },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            return this.http.post<AuthResponse>(`${this.baseUrl}/signup`, {
              email,
              password,
              ...volunteerData,
            });
          }
          return throwError(() => err);
        }),
        tap((res) => {
          this.tokenService.setToken(res.token);
          this.userSubject.next(res.user);
        }),
      )
      .toPromise() as Promise<AuthResponse>;
  }

  logout(): Promise<void> {
    this.tokenService.clearToken();
    this.userSubject.next(null);
    try {
      localStorage.removeItem('abaq_onboarding_state');
      sessionStorage.removeItem('abaq_play_completion_anim');
    } catch {
      // ignore
    }
    return Promise.resolve();
  }

  getCurrentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  getUserInfo(): Observable<any> {
    const user = this.getCurrentUser();
    if (user) {
      if (user.role === 'volunteer') {
        return this.volunteerService.getMyProfile().pipe(
          catchError(() => of(null)),
        );
      }
      return this.studentService.getStudentByAuthId(user.id).pipe(
        catchError(() => of(null)),
      );
    }
    return this.user$.pipe(
      switchMap((u) => {
        if (u) {
          if (u.role === 'volunteer') {
            return this.volunteerService.getMyProfile().pipe(
              catchError(() => of(null)),
            );
          }
          return this.studentService.getStudentByAuthId(u.id).pipe(
            catchError(() => of(null)),
          );
        }
        return of(null);
      }),
    );
  }

  loadMe(): Observable<AuthUser | null> {
    const token = this.tokenService.getToken();
    if (!token) {
      return of(null);
    }

    return this.http.get<{ user: AuthUser }>(`${this.baseUrl}/me`).pipe(
      timeout({ first: 3500 }),
      tap((res) => {
        if (res?.user) {
          this.userSubject.next(res.user);
        }
      }),
      map((res) => res?.user || null),
      catchError((err) => {
        // Solo limpiar token si el backend respondió explícitamente 401 Unauthorized
        if (err?.status === 401) {
          this.tokenService.clearToken();
          this.userSubject.next(null);
        }
        return of(null);
      }),
    );
  }

  checkExpedienteStatus(folio?: string, email?: string): Observable<any> {
    const params: Record<string, string> = {};
    if (folio) params['folio'] = folio;
    if (email) params['email'] = email;
    return this.http.get<any>(`${this.baseUrl}/expediente-status`, { params }).pipe(
      timeout({ first: 3500 }),
      catchError(() => of({ status: 'error', found: false, data: null }))
    );
  }
}
